import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { InfoHint } from './InfoHint';
import s from './Panel.module.css';

export interface PanelProps extends HTMLAttributes<HTMLElement> {
  /** "The app says this is live" — never use it for selection (web_app_design.md §2). */
  glow?: boolean;
  /** "You selected this". */
  accented?: boolean;
  sunken?: boolean;
  clip?: boolean;
  as?: 'section' | 'div' | 'article';
}

/** The bordered surface every block of content sits on. */
export function Panel({ glow, accented, sunken, clip, as: Tag = 'section', className, ...rest }: PanelProps) {
  return (
    <Tag
      className={cx(s.panel, sunken && s.sunken, glow && s.glow, accented && s.accented, clip && s.clip, className)}
      {...rest}
    />
  );
}

export interface PanelHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** One-paragraph explanation behind an info icon. */
  info?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
}

export function PanelHeader({ title, subtitle, info, actions, flush }: PanelHeaderProps) {
  return (
    <header className={cx(s.header, flush && s.headerFlush)}>
      <div className={s.titleBlock}>
        <h2 className={s.title}>{title}</h2>
        {subtitle && <span className={s.subtitle}>{subtitle}</span>}
      </div>
      {info && <InfoHint>{info}</InfoHint>}
      {actions && <div className={s.actions}>{actions}</div>}
    </header>
  );
}

export function PanelBody({ flush, className, ...rest }: HTMLAttributes<HTMLDivElement> & { flush?: boolean }) {
  return <div className={cx(s.body, flush && s.bodyFlush, className)} {...rest} />;
}

/** The grey note line under a panel: limitations, units, legends. */
export function PanelFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <footer className={cx(s.footer, className)} {...rest} />;
}
